import { posRuntimeDb } from "./pos-db";
import { ensurePosStoragePersistent } from "./storage-durability";
import { internTerminalIdentity } from "./terminal-identity-intern";
import {
  readDedicatedTerminalCredential,
  writeDedicatedTerminalCredential,
} from "./terminal-trust-store";

const terminalCredentialKey = "terminal:credential";
const legacyRuntimeDbName = "more-pos-runtime-v1";
let terminalPersistenceQueue: Promise<void> = Promise.resolve();

export type TrustedTerminalIdentity = {
  organization_id: string;
  node_id: string;
  node_code: string;
  node_name: string;
  node_type: string;
  node_status: string;
  branch_id: string;
  branch_code: string;
  branch_name: string;
  commerce_channel_id: string;
  commerce_channel_code: string;
  credential_id: string;
  credential_version: number;
  credential_status: string;
};

export type TerminalCredentialState = {
  deviceSecret: string;
  identity: TrustedTerminalIdentity | null;
  verifiedAt: string | null;
};

function terminalScope(identity: TrustedTerminalIdentity | null) {
  if (!identity) return "device:pending";
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function stableState(state: TerminalCredentialState): TerminalCredentialState {
  return {
    deviceSecret: state.deviceSecret,
    identity: state.identity ? internTerminalIdentity(state.identity) : null,
    verifiedAt: state.verifiedAt,
  };
}

function stateFromUnknown(value: unknown): TerminalCredentialState | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<TerminalCredentialState>;
  if (typeof candidate.deviceSecret !== "string" || !candidate.deviceSecret) return null;
  return stableState({
    deviceSecret: candidate.deviceSecret,
    identity: (candidate.identity as TrustedTerminalIdentity | null | undefined) ?? null,
    verifiedAt: typeof candidate.verifiedAt === "string" ? candidate.verifiedAt : null,
  });
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IDB_REQUEST_FAILED"));
  });
}

async function readLegacyTerminalCredentialRaw(): Promise<TerminalCredentialState | null> {
  if (typeof indexedDB === "undefined") return null;

  const db = await new Promise<IDBDatabase | null>((resolve) => {
    const request = indexedDB.open(legacyRuntimeDbName);
    let createdByProbe = false;

    request.onupgradeneeded = () => {
      createdByProbe = true;
    };
    request.onsuccess = () => {
      if (!createdByProbe) {
        resolve(request.result);
        return;
      }

      // An aborted first-open upgrade can leave Chromium 69 waiting indefinitely when
      // Dexie opens the real runtime schema immediately afterwards. This database is
      // known to be newly created and empty, so close and remove it cleanly first.
      request.result.close();
      const cleanup = indexedDB.deleteDatabase(legacyRuntimeDbName);
      cleanup.onsuccess = () => resolve(null);
      cleanup.onerror = () => resolve(null);
      cleanup.onblocked = () => resolve(null);
    };
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  if (!db) return null;
  try {
    if (!db.objectStoreNames.contains("runtimeMeta")) return null;
    const transaction = db.transaction("runtimeMeta", "readonly");
    const record = await requestValue(transaction.objectStore("runtimeMeta").get(terminalCredentialKey)) as { value?: unknown } | undefined;
    return stateFromUnknown(record?.value);
  } catch {
    return null;
  } finally {
    db.close();
  }
}

async function writeLegacyCompatibilityCopy(state: TerminalCredentialState) {
  try {
    await posRuntimeDb.runtimeMeta.put({
      key: terminalCredentialKey,
      scopeKey: terminalScope(state.identity),
      value: state,
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch {
    // The legacy runtime DB is a rollout compatibility copy only. Once the dedicated
    // terminal trust store succeeds, a projection DB upgrade/lock must never bounce the
    // operator back to Terminal Activation after Core already rotated the credential.
    return false;
  }
}

function sameCredential(actual: TerminalCredentialState | null, expected: TerminalCredentialState) {
  if (!actual || actual.deviceSecret !== expected.deviceSecret) return false;
  if (!expected.identity) return true;
  return (
    actual.identity?.credential_id === expected.identity.credential_id &&
    actual.identity?.credential_version === expected.identity.credential_version &&
    actual.identity?.node_id === expected.identity.node_id &&
    actual.identity?.branch_id === expected.identity.branch_id
  );
}

function verifiedAtEpoch(state: TerminalCredentialState) {
  if (!state.verifiedAt) return Number.NEGATIVE_INFINITY;
  const value = Date.parse(state.verifiedAt);
  return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
}

function preferredRecoveredCredential(
  dedicated: TerminalCredentialState,
  legacy: TerminalCredentialState,
) {
  if (dedicated.deviceSecret === legacy.deviceSecret) {
    if (!dedicated.identity && legacy.identity) return legacy;
    return verifiedAtEpoch(legacy) > verifiedAtEpoch(dedicated) ? legacy : dedicated;
  }

  // Both copies are snapshots produced by the same Trusted Terminal owner flow. During
  // the native-IndexedDB rollout an older dedicated copy can become readable again and
  // mask a newer legacy copy that the running POS had already verified with Core. When
  // both copies carry verified identities, the later Core-verified snapshot is the local
  // recovery authority. For the same node, a higher credential version is stronger than
  // timestamp ordering because credential versions are monotonic within that node.
  if (!dedicated.identity || !legacy.identity) return dedicated;

  const sameNode =
    dedicated.identity.organization_id === legacy.identity.organization_id &&
    dedicated.identity.node_id === legacy.identity.node_id;

  if (
    sameNode &&
    dedicated.identity.credential_version !== legacy.identity.credential_version
  ) {
    return legacy.identity.credential_version > dedicated.identity.credential_version
      ? legacy
      : dedicated;
  }

  return verifiedAtEpoch(legacy) > verifiedAtEpoch(dedicated) ? legacy : dedicated;
}

function staleTrustedWrite(
  current: TerminalCredentialState | null,
  incoming: TerminalCredentialState,
) {
  if (!current || !incoming.identity || current.deviceSecret === incoming.deviceSecret) return false;

  // A pending secret means a newer ISSUE/ROTATE handoff has already started. An older
  // in-flight verify must never replace that pending credential when its network response
  // arrives late.
  if (!current.identity) return true;

  if (
    current.identity.organization_id !== incoming.identity.organization_id ||
    current.identity.node_id !== incoming.identity.node_id
  ) {
    return false;
  }

  if (current.identity.credential_version > incoming.identity.credential_version) return true;
  if (current.identity.credential_version < incoming.identity.credential_version) return false;

  // Same-node / same-version replacement is allowed only for the same canonical
  // credential row when the incoming snapshot was verified later. This repairs a stale
  // duplicate local copy without weakening the existing vN -> vN+1 race protection.
  if (current.identity.credential_id !== incoming.identity.credential_id) return true;
  return verifiedAtEpoch(current) >= verifiedAtEpoch(incoming);
}

async function persistTerminalCredentialNow(state: TerminalCredentialState) {
  const current = await readDedicatedTerminalCredential();
  if (staleTrustedWrite(current, state)) {
    throw new Error("TERMINAL_CREDENTIAL_SUPERSEDED");
  }

  const dedicatedWritten = await writeDedicatedTerminalCredential(state);
  const legacyWritten = await writeLegacyCompatibilityCopy(state);

  // Read-after-write from the Owner local store boundary. At least one durable copy must
  // contain the exact newly issued credential before the UI may continue as trusted.
  const readback = await readTerminalCredential();
  if (!sameCredential(readback, state)) {
    throw new Error(
      dedicatedWritten || legacyWritten
        ? "TERMINAL_LOCAL_READBACK_MISMATCH"
        : "TERMINAL_LOCAL_PERSISTENCE_FAILED",
    );
  }

  return readback;
}

function persistTerminalCredential(state: TerminalCredentialState) {
  const run = terminalPersistenceQueue.then(
    () => persistTerminalCredentialNow(state),
    () => persistTerminalCredentialNow(state),
  );

  terminalPersistenceQueue = run.then(
    () => undefined,
    () => undefined,
  );

  return run;
}

function protectTrustedStorage(state: TerminalCredentialState | null) {
  if (state?.identity) void ensurePosStoragePersistent();
  return state;
}

export async function readTerminalCredential(): Promise<TerminalCredentialState | null> {
  // Device trust has a lifecycle different from rebuildable catalog/runtime projections.
  // The dedicated DB remains the primary store, but rollout compatibility can leave two
  // durable copies. Read both before returning so a stale dedicated snapshot cannot mask
  // a newer Core-verified legacy snapshot after a browser/app restart.
  const dedicated = await readDedicatedTerminalCredential();
  const rawLegacy = await readLegacyTerminalCredentialRaw();

  if (dedicated && rawLegacy) {
    const stableDedicated = stableState(dedicated);
    const stableLegacy = stableState(rawLegacy);
    const preferred = preferredRecoveredCredential(stableDedicated, stableLegacy);

    if (preferred === stableLegacy) {
      // Repair the primary local owner copy. This does not create authority: the selected
      // state already contains a prior Core-verified identity and will still be revalidated
      // online by the existing Owner Contract before becoming `verified` for this boot.
      await writeDedicatedTerminalCredential(preferred);
    }

    return protectTrustedStorage(preferred);
  }

  if (dedicated) return protectTrustedStorage(stableState(dedicated));

  // Recovery path for terminals enrolled before the dedicated trust DB existed. Read the
  // legacy record with native IndexedDB first, so a Dexie projection migration cannot hide
  // an otherwise valid Device Secret / Trusted Identity.
  if (rawLegacy) {
    await writeDedicatedTerminalCredential(rawLegacy);
    return protectTrustedStorage(rawLegacy);
  }

  // Last compatibility fallback when the legacy runtime DB is already open through Dexie.
  try {
    const record = await posRuntimeDb.runtimeMeta.get(terminalCredentialKey);
    const legacy = stateFromUnknown(record?.value);
    if (!legacy) return null;
    await writeDedicatedTerminalCredential(legacy);
    return protectTrustedStorage(legacy);
  } catch {
    return null;
  }
}

export async function savePendingTerminalSecret(deviceSecret: string) {
  const state = { deviceSecret, identity: null, verifiedAt: null } satisfies TerminalCredentialState;
  return persistTerminalCredential(state);
}

export async function saveTrustedTerminal(
  deviceSecret: string,
  identity: TrustedTerminalIdentity,
) {
  const stableIdentity = internTerminalIdentity(identity);
  const state = {
    deviceSecret,
    identity: stableIdentity,
    verifiedAt: new Date().toISOString(),
  } satisfies TerminalCredentialState;

  const readback = await persistTerminalCredential(state);
  void ensurePosStoragePersistent();
  return readback;
}
